import { AfterViewInit, asNativeElements, ChangeDetectorRef, Component, ContentChild, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyInputComponent } from './currency-input.component';
import { PercentInputComponent } from './percent-input.component';

@Component({
    standalone: true,
    selector: 'dai-form-group',
    template: `<div [class]="classList()">
    @if (!isCheckbox) {
    <label [for]="for" class="col-md-2 col-form-label" [class]="hasError() ? 'text-danger' : ''">{{label}}{{required() ? '*' : ''}}</label>
    }
    <div class="col-md-10" [class]="isCheckbox ? 'offset-md-2 form-check px-5' : ''">
        <ng-content #inputRef></ng-content>
        @if (isCheckbox) {
        <label [for]="for" class="form-label" [class]="hasError() ? 'text-danger' : ''">{{label}}{{required() ? '*' : ''}}</label>
        }
        @if (hasError()) {
        <div class="text-danger">
            {{getError()}}
        </div>
    }
    </div>
    </div>`,
    imports: []

})
export class FormGroupComponent implements OnInit, AfterViewInit {
    @Input() controller?: AbstractControl;
    @Input() label: string = 'Unknown';
    @Input() for: string = ''

    @ContentChild('inputRef', { static: false }) inputElement: ElementRef<HTMLInputElement> | ElementRef<HTMLTextAreaElement> | CurrencyInputComponent | PercentInputComponent | null = null;

    public isCheckbox = false;

    private rawInputElement: HTMLInputElement | HTMLTextAreaElement | null = null;

    constructor(private cd: ChangeDetectorRef) { }

    ngOnInit(): void {
        if (this.controller) {
            this.controller.statusChanges.subscribe({
                next: (status) => {
                    this.updateInputStatus(status);
                }
            });
        }
    }

    private updateInputStatus(status: string) {
        if (this.rawInputElement) {
            if (this.hasError()) {
                this.rawInputElement.classList.add("is-invalid");
            }
            else {
                this.rawInputElement.classList.remove("is-invalid");
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.inputElement instanceof ElementRef) {
            let { nativeElement } = this.inputElement;
            if (nativeElement.nodeName === "INPUT" || nativeElement.nodeName === "TEXTAREA" || nativeElement.nodeName === "SELECT") {
                this.rawInputElement = nativeElement;
            }
        }
        else if (this.inputElement instanceof CurrencyInputComponent || this.inputElement instanceof PercentInputComponent) {
            this.rawInputElement = this.inputElement.inputElement.nativeElement;
        }

        if (this.rawInputElement) {
            const nativeElement = this.rawInputElement;
            if (nativeElement.nodeName === "INPUT") {
                this.isCheckbox = nativeElement.type === "checkbox";

                if (this.isCheckbox) {
                    nativeElement.classList.add("form-check-input");
                }
                else {
                    nativeElement.classList.add("form-control");
                }

                nativeElement.required = this.required();
            }
            else if (nativeElement.nodeName === "SELECT") {
                nativeElement.classList.add("form-control");
                nativeElement.required = this.required();
            }
            else if (nativeElement.nodeName === "TEXTAREA") {
                nativeElement.classList.add("form-control");
                nativeElement.required = this.required();
            }
        }

        this.updateInputStatus(this.controller?.status ?? "");

        this.cd.detectChanges();
    }

    required(): boolean {
        return this.controller?.hasValidator(Validators.required) ?? false
    }

    disabled(): boolean {
        return this.controller?.disabled ?? false;
    }

    hasError(): boolean {
        return (this.controller?.invalid ?? false) && (this.controller?.touched ?? false);
    }

    getError(): string | null {
        const errors = this.controller?.errors || {};
        if (errors['required']) {
            return `${this.label} is required`;
        }
        else if (errors['minlength']) {
            return `${this.label} must be at least ${errors['minlength'].requiredLength} characters long.`;
        }
        else if (errors['maxlength']) {
            return `${this.label} can not exceed ${errors['maxlength'].requiredLength} characters.`;
        }
        else if (errors['min']) {
            return `${this.label} must have a minimal value of ${errors["min"].min}.`;
        }
        else if (errors['max']) {
            return `${this.label} can not exceed a value of ${errors["max"].max}.`;
        }
        else if (errors['email']) {
            return `${this.label} must be a valid email address.`;
        }
        else if (errors) {
            return `Unknown validation error: ${JSON.stringify(errors)}`;
        }

        return null;
    }

    classList(): string[] {
        const classes = ["mb-3", "row"];
        if (this.isCheckbox) {
            //classes.push("form-check");
        }
        return classes;
    }
}
